import {type RefObject, useEffect, useRef, useState} from 'react';

const SCROLL_THRESHOLD = 1;

export type ScrollableAreaState = {
  canScrollUp: boolean;
  canScrollDown: boolean;
};

export function getScrollableAreaState(element: HTMLElement): ScrollableAreaState {
  const {scrollTop, scrollHeight, clientHeight} = element;
  const canScrollUp = scrollTop > SCROLL_THRESHOLD;
  const canScrollDown = scrollTop + clientHeight < scrollHeight - SCROLL_THRESHOLD;
  return {canScrollUp, canScrollDown};
}

export type UseScrollableAreaOptions = {
  ref?: RefObject<HTMLElement | null>;
};

export type UseScrollableAreaResult<T extends HTMLElement = HTMLDivElement> = {
  ref: RefObject<T | null>;
  canScrollUp: boolean;
  canScrollDown: boolean;
};

export function useScrollableArea<T extends HTMLElement = HTMLDivElement>(
  options: UseScrollableAreaOptions = {},
): UseScrollableAreaResult<T> {
  const internalRef = useRef<T>(null);
  const ref = (options.ref ?? internalRef) as RefObject<HTMLElement | null>;
  const [state, setState] = useState({canScrollUp: false, canScrollDown: false});

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateState = () => {
      const newState = getScrollableAreaState(element);
      setState(prev =>
        prev.canScrollUp !== newState.canScrollUp ||
        prev.canScrollDown !== newState.canScrollDown
          ? newState
          : prev,
      );
    };

    updateState();

    const resizeObserver = new ResizeObserver(updateState);
    resizeObserver.observe(element);

    element.addEventListener('scroll', updateState, {passive: true});

    return () => {
      resizeObserver.disconnect();
      element.removeEventListener('scroll', updateState);
    };
  }, [ref]);

  return {
    ref: ref as RefObject<T | null>,
    ...state,
  };
}
